import { useCallback } from 'react';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { ProductFeatureName } from 'dashboard/models/organization';
import useProductFeature from 'lib/hooks/useProductFeature';

/**
 * @name `useHasAccessForTierDeterminator`
 *
 * A hook that returns a function to determine whether the current user/org
 * should have access to a given feature based on its product feature tier.
 *
 * Used in the useIsEditableWithOptions to determine edit access and by itself to
 * conditionally show 'Limited' badging agnostic of current 'editable state,
 * e.g: should not show badging for an enhanced org user lacking VT edit permissions
 * if the expected access tier is inquiries basic - useIsEditableWithOptions returns false,
 * but this hook should still return true.
 */
export default () => {
  const isInquiriesFree = useProductFeature(ProductFeatureName.InquiriesFree);
  const isInquiriesBasic = useProductFeature(ProductFeatureName.InquiriesBasic);
  const isInquiriesEnhanced = useProductFeature(ProductFeatureName.InquiriesEnhanced);

  return useCallback(
    (accessTier: EditorAccessTier | undefined) => {
      // if providing an access tier, verify that any of the product features at that level or above
      // are enabled (sometimes orgs with higher level access don't always have the lower ones "backfilled")
      switch (accessTier) {
        case EditorAccessTier.Any:
          return true;
        case EditorAccessTier.InquiriesFree:
          return isInquiriesFree || isInquiriesBasic || isInquiriesEnhanced;
        case EditorAccessTier.InquiriesBasic:
          return isInquiriesBasic || isInquiriesEnhanced;
        case EditorAccessTier.InquiriesEnhanced:
          return isInquiriesEnhanced;
        default:
          return false;
      }
    },
    [isInquiriesFree, isInquiriesBasic, isInquiriesEnhanced],
  );
};
